import { useState } from "react";
import { Button, Card, Input, Tag } from "antd";
import type { AllBuildingBlocks, Assumption, WMBT } from "../types";
import { BUILDING_BLOCK_LABELS } from "../schema";

// Map alternate / legacy wmbt shapes onto the canonical fields the panel
// reads (label / value / category / ...). We do this PER-ITEM rather than
// using normalizeWmbtItems because that helper drops items with no
// meaningful content — but the backend assumption handler indexes into the
// raw wmbt array by itemIndex, so dropping a sparse item here would shift
// indices and target the wrong row server-side.
//
// Known shapes folded forward:
// - {description, isAssumption, source} ← research-integration (Perplexity) output
// - {text, isAssumption, source}        ← research-integration AI output
// - {objective, ...}                    ← earliest legacy schema
// - {category, label, value, ...}       ← current canonical schema
function coerceWmbtFields(item: any): WMBT {
  return {
    category: item.category || "",
    categories: item.categories || undefined,
    label: item.label || item.objective || item.text || item.description || "",
    value: item.value ?? null,
    isAssumption: item.isAssumption ?? false,
    assumptionExplanation: item.assumptionExplanation ?? null,
    source: item.source ?? null,
    userValidated: item.userValidated ?? false,
    categoryObjective: item.categoryObjective ?? null,
  };
}
import { X, Pencil, Loader2, Mic, MicOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useMicRecorder, RecordingObjectType } from "@vojomo-core/shared";

type PendingAction = { type: "approve" | "reject" | "modify"; modifiedValue?: string };

interface AssumptionsPanelProps {
  buildingBlocks: AllBuildingBlocks;
  actions: Record<string, PendingAction>;
  onActionSet: (key: string, action: PendingAction | null) => void;
  onValidateAssumption?: (blockKey: string, itemIndex: number, validated: boolean) => Promise<void>;
}

export function extractAssumptions(blocks: AllBuildingBlocks): Assumption[] {
  const assumptions: Assumption[] = [];
  const blockKeys = [
    "peakAmbitionObjectives",
    "threeYearObjectives",
    "oneYearObjectives",
    "quarterlyObjectives",
  ] as const;

  for (const key of blockKeys) {
    const block = blocks[key];
    if (!block?.wmbt) continue;
    block.wmbt.forEach((rawItem, index) => {
      const item = coerceWmbtFields(rawItem);
      if (item.isAssumption) {
        assumptions.push({
          blockKey: key,
          blockLabel: BUILDING_BLOCK_LABELS[key]?.label || key,
          itemIndex: index,
          item,
        });
      }
    });
  }

  return assumptions;
}

export function AssumptionsPanel({
  buildingBlocks,
  actions,
  onActionSet,
  onValidateAssumption,
}: AssumptionsPanelProps) {
  const assumptions = extractAssumptions(buildingBlocks);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const { isRecording, startRecording, stopRecording, isInitializing, isProcessingAudio } = useMicRecorder();

  const getKey = (a: Assumption) => `${a.blockKey}-${a.itemIndex}`;

  const handleApprove = (a: Assumption) => {
    onActionSet(getKey(a), { type: "approve" });
    onValidateAssumption?.(a.blockKey, a.itemIndex, true);
  };

  const handleReject = (a: Assumption) => {
    onActionSet(getKey(a), { type: "reject" });
    onValidateAssumption?.(a.blockKey, a.itemIndex, false);
  };

  const handleStartModify = (a: Assumption) => {
    const key = getKey(a);
    setEditingKey(key);
    setEditValue(a.item.value || "");
  };

  const handleConfirmModify = (a: Assumption) => {
    onActionSet(getKey(a), { type: "modify", modifiedValue: editValue });
    setEditingKey(null);
    setEditValue("");
  };

  const handleUndo = (a: Assumption) => {
    onActionSet(getKey(a), null);
  };

  return (
    <div>
      <div className="px-3 pt-3 pb-2 bg-white border-b border-slate-100 shrink-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-base font-semibold leading-snug">Assumptions</div>
            <p className="text-xs text-muted-foreground">
              Approve, reject, or modify each assumption, then apply changes.
            </p>
          </div>
          {assumptions.length > 0 && <Tag className="text-xs shrink-0">{assumptions.length}</Tag>}
        </div>
      </div>

      {assumptions.length === 0 ? (
        <div className="px-4 pt-4">
          <div className="flex items-center justify-center py-8 border border-dashed rounded-md bg-muted/20">
            <p className="text-xs text-muted-foreground">
              No assumptions yet. As Vo drafts your roadmap, assumptions will appear here for review.
            </p>
          </div>
        </div>
      ) : (
      <div className="px-4 pt-3 pb-4 space-y-2">
        <AnimatePresence mode="popLayout">
          {assumptions.map((assumption) => {
            const key = getKey(assumption);
            const action = actions[key];
            const isEditing = editingKey === key;

            const isValidated = assumption.item.userValidated && !action;

            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                data-testid={`assumption-${key}`}
              >
                <Card
                  styles={{ body: { padding: 12 } }}
                  className={`transition-colors ${
                    action?.type === "approve" || isValidated
                      ? "border-emerald-300 bg-emerald-50/50"
                      : action?.type === "reject"
                        ? "border-red-300 bg-red-50/50"
                        : action?.type === "modify"
                          ? "border-blue-300 bg-blue-50/50"
                          : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Tag className="text-[10px] py-0">{assumption.blockLabel}</Tag>
                        <span className="text-[10px] text-muted-foreground capitalize">
                          {assumption.item.category?.replace("_", " ")}
                        </span>
                        {isValidated && (
                          <Tag color="success" className="text-[10px] py-0">Validated</Tag>
                        )}
                      </div>
                      <p className="text-sm font-medium">
                        {assumption.item.label || assumption.item.categoryObjective || "Unnamed assumption"}
                      </p>
                      {isEditing ? (
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="text-sm h-8"
                            autoFocus
                            data-testid={`input-modify-${key}`}
                          />
                          <Button
                            type="text"
                            size="small"
                            style={{ padding: "0 6px", height: 32, flexShrink: 0 }}
                            className={
                              isRecording && editingKey === key
                                ? "text-red-500 hover:text-red-600"
                                : "text-muted-foreground hover:text-foreground"
                            }
                            disabled={isInitializing || isProcessingAudio}
                            onClick={() => {
                              if (isRecording && editingKey === key) {
                                stopRecording();
                              } else {
                                startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
                                  setEditValue(transcript);
                                });
                              }
                            }}
                            title={isRecording && editingKey === key ? "Stop recording" : "Voice input"}
                          >
                            {isProcessingAudio && editingKey === key
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : isRecording && editingKey === key
                              ? <MicOff className="h-3.5 w-3.5" />
                              : <Mic className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            className="h-8 text-xs"
                            onClick={() => handleConfirmModify(assumption)}
                          >
                            Save
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            className="h-8 text-xs"
                            onClick={() => setEditingKey(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          {action?.type === "modify" ? (
                            <p className="text-sm text-blue-600 mt-0.5">{action.modifiedValue}</p>
                          ) : (
                            assumption.item.value && (
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {assumption.item.value}
                              </p>
                            )
                          )}
                        </>
                      )}
                      {assumption.item.assumptionExplanation && !isEditing && (
                        <p className="text-xs text-amber-600 mt-1 italic">
                          {assumption.item.assumptionExplanation}
                        </p>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="flex items-center gap-1 shrink-0">
                        {action ? (
                          <Button
                            type="text"
                            size="small"
                            className="h-7 text-xs"
                            onClick={() => handleUndo(assumption)}
                          >
                            Undo
                          </Button>
                        ) : (
                          <>
                            <Button
                              type="text"
                              size="small"
                              style={{ padding: "0 4px", height: 28 }}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleApprove(assumption)}
                              title="Approve"
                              data-testid={`button-approve-${key}`}
                            >
                              ✓
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              style={{ padding: "0 4px", height: 28 }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleReject(assumption)}
                              title="Reject"
                              data-testid={`button-reject-${key}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              style={{ padding: "0 4px", height: 28 }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleStartModify(assumption)}
                              title="Modify"
                              data-testid={`button-modify-${key}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      )}
    </div>
  );
}
